import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useClients } from '../context/ClientContext';
import { useTableState } from './useTableState';
import type { FetchPageOptions } from '../infrastructure/apiClient';

interface FetchResponse<T> {
  data?: T[];
  total?: number;
}

export interface UseDashboardDataOptions<T extends object> {
  fetchFn: (tenantId: string, options: FetchPageOptions) => Promise<FetchResponse<T>>;
  entityName: string;
}

export function useDashboardData<T extends object>({
  fetchFn,
  entityName,
}: UseDashboardDataOptions<T>) {
  const { activeClient, loading: tenantsLoading } = useClients();
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const entityNameRef = useRef(entityName);
  entityNameRef.current = entityName;

  const fetchData = useCallback(
    async (tenantId: string, { offset = 0, limit = 10, signal }: FetchPageOptions = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchFnRef.current(tenantId, { offset, limit, signal });
        const list = response?.data ?? [];
        setData(list);
        updateTotal(typeof response?.total === 'number' ? response.total : offset + list.length);
        if (offset === 0) {
          setSorter({} as never);
        }
      } catch (err) {
        if (!signal?.aborted) {
          console.error(`Failed to load ${entityNameRef.current}`, err);
          setData([]);
          resetPagination();
          setError(`Unable to load ${entityNameRef.current} for this tenant.`);
          message.error(`Failed to load ${entityNameRef.current}`);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const handlePageChange = useCallback(
    (offset: number, limit: number) => {
      if (!activeClient?.id) return;
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      fetchData(activeClient.id, { offset, limit, signal: controller.signal });
    },
    [activeClient?.id, fetchData],
  );

  const {
    pagination,
    sorter,
    setSorter,
    tablePagination,
    handleTableChange,
    resetPagination,
    updateTotal,
    updateCurrentPage,
  } = useTableState<T>({ onPageChange: handlePageChange });

  useEffect(() => {
    if (!activeClient?.id) {
      setData([]);
      resetPagination();
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchData(activeClient.id, { offset: 0, limit: pagination.pageSize, signal: controller.signal });
    return () => controller.abort();
  }, [activeClient?.id, fetchData, pagination.pageSize, resetPagination]);

  const disableActions = !activeClient?.id || tenantsLoading;

  return {
    data,
    setData,
    loading,
    setLoading,
    error,
    activeClient,
    tenantsLoading,
    pagination,
    sorter,
    setSorter,
    tablePagination,
    handleTableChange,
    updateCurrentPage,
    fetchData,
    abortControllerRef,
    disableActions,
  };
}
